import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { projects as staticProjects } from "@/data/projects";

// Default image for projects
import defaultProjectImage from "@/assets/blog/indoor-air-quality-family.jpg";
export const RecentProjects = () => {
  // Fetch database projects
  const {
    data: dbProjects
  } = useQuery({
    queryKey: ['recent-projects'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('projects').select('*').eq('published', true).order('created_at', {
        ascending: false
      }).limit(3);
      if (error) throw error;
      return data;
    }
  });

  // Combine database projects with static projects
  const allProjects = [...(dbProjects || []), ...staticProjects].slice(0, 6);
  return <section className="py-20 bg-muted/30" aria-labelledby="projects-heading">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/10 text-secondary font-medium text-sm mb-4">
            Our Work
          </span>
          <h2 id="projects-heading" className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Our Recent Projects
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">See examples of the quality work we deliver to our neighbors throughout Minnesota.</p>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full" aria-label="Recent projects carousel">
          <CarouselContent className="-ml-4">
            {allProjects.map(project => <CarouselItem key={project.slug} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <Link to={`/projects/${project.slug}`} className="feature-card h-full flex flex-col group hover:border-secondary/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 overflow-hidden p-0">
                  {/* Project Image */}
                  <div className="aspect-video overflow-hidden bg-muted">
                    <img src={project.image_url || defaultProjectImage} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="mb-3">
                      <span className="text-sm font-medium text-secondary">{project.location}</span>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground mb-2 group-hover:text-secondary transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 flex-1 line-clamp-2">
                      {project.description.slice(0, 100)}...
                    </p>
                    <ul className="space-y-1.5 mb-4" aria-label="Project highlights">
                      {project.highlights.slice(0, 2).map((highlight, i) => <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0" aria-hidden="true" />
                          {highlight}
                        </li>)}
                    </ul>
                    <div className="flex items-center text-secondary font-semibold text-sm">
                      View Project <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
                    </div>
                  </div>
                </Link>
              </CarouselItem>)}
          </CarouselContent>
          <div className="flex items-center justify-center gap-4 mt-8">
            <CarouselPrevious className="static translate-y-0" aria-label="View previous projects" />
            <CarouselNext className="static translate-y-0" aria-label="View next projects" />
          </div>
        </Carousel>

        <div className="text-center mt-8">
          <Link to="/projects">
            <Button variant="outline" className="font-semibold">
              View All Projects <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
};